import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import PageContainer from '../Components/PageContainer';
import useGetProcessedTimesheetDetails from '../hooks/useGetProcessedTimesheetDetails';
import ProcessedTimesheetDetailsTable from '../Components/ProcessedTimesheets/ProcessedTimesheetDetailsTable';
import PartialInvoiceBanner from '../Components/ProcessedTimesheets/PartialInvoiceBanner';
import LocationMismatchBanner from '../Components/ProcessedTimesheets/LocationMismatchBanner';
import { isPtsReadOnly } from '../Configurations/processedTimesheetsConfig';
import {
    ProcessedTimesheetContextProvider,
    useProcessedTimesheetContext,
} from '../Contexts/ProcessedTimesheetContext';
import useAdminAuthContext from '../hooks/useAdminAuthContext';
import useRedirectToAggregatePts from '../hooks/useRedirectToAggregatePts';

const useInitialSortQueryParams = () => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const field = params.get('sortField') || 'date';
    const sort = params.get('sortDirection') || 'asc';
    return [{ field, sort }];
};

const ProcessedTimesheetDetailsTablePage = () => {
    const { id: processedTimesheetId } = useParams();
    const {
        data: timesheetDetailsData,
        isLoading,
        isFetching,
        refetch,
        isError,
    } = useGetProcessedTimesheetDetails({
        processedTimesheetId,
    });
    useRedirectToAggregatePts({
        processedTimesheetId,
        timesheetDetailsData,
        isLoading,
        isFetching,
    });
    const initialSortModel = useInitialSortQueryParams();
    const { hasRecentChanges } = useProcessedTimesheetContext();
    const { ltoeApiTimesheetEnableEditApprovedTimesheet } = useFlags();
    const { isTimesheetSystemAdminUser } = useAdminAuthContext();
    const readOnly =
        isPtsReadOnly(timesheetDetailsData?.status) ||
        hasRecentChanges ||
        isLoading ||
        isError ||
        !ltoeApiTimesheetEnableEditApprovedTimesheet ||
        !isTimesheetSystemAdminUser;

    const locations = useMemo(() => {
        if (!timesheetDetailsData?.timeEntries?.entriesByLocation) return [];

        return timesheetDetailsData.timeEntries.entriesByLocation.map((locationTimeEntry) => ({
            locationName: locationTimeEntry.locationName,
            locationId: locationTimeEntry.locationId,
        }));
    }, [timesheetDetailsData?.timeEntries?.entriesByLocation]);

    const weekEndingStartDate = useMemo(
        () =>
            timesheetDetailsData?.weekEndingDate
                ? moment(timesheetDetailsData?.weekEndingDate).subtract(6, 'days')
                : null,
        [timesheetDetailsData?.weekEndingDate]
    );
    const weekEndingEndDate = useMemo(
        () =>
            timesheetDetailsData?.weekEndingDate
                ? moment(timesheetDetailsData?.weekEndingDate)
                : null,
        [timesheetDetailsData?.weekEndingDate]
    );

    return (
        <PageContainer>
            <Box sx={{ paddingRight: '30px' }}>
                <Stack gap={2}>
                    <LocationMismatchBanner timesheetDetails={timesheetDetailsData} />
                    <PartialInvoiceBanner
                        invoiceStatus={timesheetDetailsData?.invoiceStatus}
                        unInvoicedTotalToBill={timesheetDetailsData?.totals?.unInvoicedTotalToBill}
                        processedTimesheetEntries={timesheetDetailsData?.processedTimesheetEntries}
                    />
                </Stack>
                <ProcessedTimesheetDetailsTable
                    isLoading={isLoading}
                    processedTimesheetId={processedTimesheetId}
                    data={timesheetDetailsData?.processedTimesheetEntries || []}
                    availableRates={timesheetDetailsData?.availableRates || []}
                    weekEndingStartDate={weekEndingStartDate}
                    weekEndingEndDate={weekEndingEndDate}
                    initialSortModel={initialSortModel}
                    refetch={refetch}
                    locations={locations}
                    locationsPTSIds={timesheetDetailsData?.locationPTSIds ?? []}
                    readOnly={readOnly}
                    splitByWorkLocation={timesheetDetailsData?.splitByWorkLocation}
                    hideOpenButton
                />
            </Box>
        </PageContainer>
    );
};

const ProcessedTimesheetDetailsTablePageWithContext = () => (
    <ProcessedTimesheetContextProvider>
        <ProcessedTimesheetDetailsTablePage />
    </ProcessedTimesheetContextProvider>
);

export default ProcessedTimesheetDetailsTablePageWithContext;
